/**
 * BrandKitPage — admin-only. Configure Logo + Frame + Text overlay per company
 * and per platform (Instagram, Facebook, TikTok, LinkedIn).
 *
 * Backed by:
 *   GET    /api/brand-kit/_formats
 *   GET    /api/brand-kit/{company_id}
 *   PUT    /api/brand-kit/{company_id}/settings
 *   PUT    /api/brand-kit/{company_id}/{platform}
 *   DELETE /api/brand-kit/{company_id}/{platform}
 *   POST   /api/brand-kit/{company_id}/upload      (multipart: file, kind)
 *   POST   /api/brand-kit/{company_id}/preview     (returns JPEG)
 *   POST   /api/brand-kit/{company_id}/ai-generate
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Upload, Save, Trash2, ImageIcon, Frame as FrameIcon,
  Sparkles, RefreshCw, Type,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_LABEL = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
};
const PLATFORM_COLOR = {
  instagram: "#E1306C",
  facebook: "#1877F2",
  tiktok: "#000000",
  linkedin: "#0A66C2",
  pinterest: "#E60023",
};
const ANCHOR_LABEL = {
  "top-left": "Oben Links", "top-center": "Oben Mitte", "top-right": "Oben Rechts",
  "middle-left": "Mitte Links", "middle-center": "Mitte", "middle-right": "Mitte Rechts",
  "bottom-left": "Unten Links", "bottom-center": "Unten Mitte", "bottom-right": "Unten Rechts",
};

const emptyTemplate = (platform) => ({
  platform,
  enabled: false,
  logo: { media_id: null, storage_path: null, anchor: "bottom-right", size_pct: 15, opacity: 1, padding_pct: 4 },
  frame: { media_id: null, storage_path: null, opacity: 1 },
  text: { content: "", anchor: "bottom-left", size_px: 48, color: "#FFFFFF", background: "#000000B3", padding_pct: 4 },
});

export default function BrandKitPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [formats, setFormats] = useState(null);
  const [kit, setKit] = useState(null);
  const [activePlatform, setActivePlatform] = useState("instagram");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const previewTimer = useRef(null);

  const template = useMemo(() => {
    if (!kit) return emptyTemplate(activePlatform);
    return kit.templates?.[activePlatform] || emptyTemplate(activePlatform);
  }, [kit, activePlatform]);

  // --- Loaders ---------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const [{ data: cs }, { data: f }] = await Promise.all([
          axios.get(`${API}/companies`, { withCredentials: true }),
          axios.get(`${API}/brand-kit/_formats`, { withCredentials: true }),
        ]);
        const list = cs.items || cs || [];
        setCompanies(list);
        setFormats(f);
        if (list.length && !companyId) setCompanyId(list[0].id);
      } catch (e) {
        toast.error("Konnte Firmen oder Formate nicht laden");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/brand-kit/${companyId}`, { withCredentials: true });
        setKit(data);
      } catch (e) {
        toast.error("Konnte Brand Kit nicht laden");
      }
    })();
  }, [companyId]);

  // --- Preview (debounced) --------------------------------------------
  const refreshPreview = async (tpl) => {
    if (!companyId) return;
    try {
      const resp = await axios.post(
        `${API}/brand-kit/${companyId}/preview`,
        { platform: activePlatform, template: tpl || template },
        { withCredentials: true, responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } catch (e) {
      // silent — preview is best-effort
    }
  };

  useEffect(() => {
    if (!kit) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => refreshPreview(), 300);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, activePlatform, companyId]);

  // --- Mutations -------------------------------------------------------
  const updateTemplate = (section, patch) => {
    setKit((prev) => {
      const next = { ...prev };
      next.templates = { ...(prev.templates || {}) };
      next.templates[activePlatform] = {
        ...emptyTemplate(activePlatform),
        ...(next.templates[activePlatform] || {}),
        [section]: {
          ...(next.templates[activePlatform]?.[section] || emptyTemplate(activePlatform)[section]),
          ...patch,
        },
      };
      return next;
    });
  };

  const toggleEnabled = (val) => {
    setKit((prev) => {
      const next = { ...prev };
      next.templates = { ...(prev.templates || {}) };
      next.templates[activePlatform] = {
        ...emptyTemplate(activePlatform),
        ...(next.templates[activePlatform] || {}),
        enabled: val,
      };
      return next;
    });
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/brand-kit/${companyId}/${activePlatform}`, template, { withCredentials: true });
      toast.success(`${PLATFORM_LABEL[activePlatform]}-Vorlage gespeichert`);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const clearTemplate = async () => {
    if (!window.confirm(`Vorlage fuer ${PLATFORM_LABEL[activePlatform]} zuruecksetzen?`)) return;
    try {
      await axios.delete(`${API}/brand-kit/${companyId}/${activePlatform}`, { withCredentials: true });
      const { data } = await axios.get(`${API}/brand-kit/${companyId}`, { withCredentials: true });
      setKit(data);
      toast.success("Vorlage zurueckgesetzt");
    } catch (e) {
      toast.error("Loeschen fehlgeschlagen");
    }
  };

  const toggleApplyByDefault = async (val) => {
    setKit((prev) => ({ ...prev, apply_by_default: val }));
    try {
      await axios.put(`${API}/brand-kit/${companyId}/settings`, { apply_by_default: val }, { withCredentials: true });
    } catch (e) {
      toast.error("Einstellung konnte nicht gespeichert werden");
    }
  };

  const uploadAsset = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await axios.post(`${API}/brand-kit/${companyId}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateTemplate(kind, { media_id: data.media_id, storage_path: data.storage_path });
      toast.success(`${kind === "logo" ? "Logo" : "Rahmen"} hochgeladen (${data.width}x${data.height}px)`);
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
    }
  };

  const runAiGenerate = async () => {
    const logoPath = template.logo?.storage_path;
    if (!logoPath && !template.text?.content) {
      toast.error("Bitte zuerst Logo oder Text der aktuellen Plattform setzen, dann auf alle uebernehmen.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/brand-kit/${companyId}/ai-generate`, {
        logo_storage_path: logoPath,
        logo_media_id: template.logo?.media_id,
        text: template.text?.content,
        text_color: template.text?.color,
        text_background: template.text?.background,
      }, { withCredentials: true });
      setKit((prev) => ({ ...prev, templates: data.templates, apply_by_default: true }));
      toast.success("Brand Kit auf alle 4 Plattformen uebernommen");
    } catch (e) {
      toast.error("KI-Uebernahme fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const platformFormat = useMemo(() => {
    if (!formats) return null;
    return formats.platforms.find((p) => p.id === activePlatform);
  }, [formats, activePlatform]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="brand-kit-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--studio-accent)]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="brand-kit-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[var(--studio-text)] flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-[var(--studio-accent)]" />
            Brand Kit
          </h1>
          <p className="text-sm text-[var(--studio-text-muted)] mt-1">
            Logo, Rahmen und Textbalken pro Firma und Plattform — wird beim Posten automatisch angewendet.
          </p>
        </div>
      </div>

      <PageHelp pageKey="brand-kit" title="So funktioniert das Brand Kit">
        <div className="space-y-2">
          <p><strong>1)</strong> Firma waehlen.</p>
          <p><strong>2)</strong> Pro Plattform (IG / FB / TikTok / LinkedIn) hochladen: <em>Logo</em> (PNG mit Transparenz) und/oder <em>Rahmen</em> (PNG ueber das ganze Bild). Optional einen <em>Textbalken</em> (z. B. <code>@strongfit_wetzikon</code>).</p>
          <p><strong>3)</strong> Position, Groesse und Opazitaet einstellen. Vorschau rechts updates live.</p>
          <p><strong>4)</strong> Plattform aktivieren (Schalter oben) und <em>Speichern</em>.</p>
          <p><strong>5)</strong> Mit <em>"Auf alle Plattformen uebernehmen"</em> kopierst du Logo + Text einheitlich nach IG / FB / TikTok / LinkedIn.</p>
          <p>Die Plattform-Pixelgroessen sind unter dem Tab-Namen sichtbar.</p>
        </div>
      </PageHelp>

      {/* Company picker + global toggle */}
      <Card className="mb-6 border-[var(--studio-border)] bg-[var(--studio-surface)]">
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--studio-text-muted)]">Firma</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="mt-2 bg-[var(--studio-bg)] border-[var(--studio-border)]" data-testid="brand-kit-company-select">
                <SelectValue placeholder="Firma waehlen..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end justify-between gap-3 p-3 rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg)]">
            <div>
              <p className="text-sm font-medium">Standardmaessig anwenden</p>
              <p className="text-xs text-[var(--studio-text-muted)]">
                Wenn aktiv, wird das Brand Kit beim Posten automatisch ueber das Bild gelegt.
              </p>
            </div>
            <Switch
              checked={kit?.apply_by_default !== false}
              onCheckedChange={toggleApplyByDefault}
              data-testid="brand-kit-apply-default-switch"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        {/* Configuration column */}
        <div>
          <Tabs value={activePlatform} onValueChange={setActivePlatform}>
            <TabsList className="grid grid-cols-5 mb-4 bg-[var(--studio-surface)]">
              {["instagram", "facebook", "tiktok", "linkedin", "pinterest"].map((p) => (
                <TabsTrigger key={p} value={p} className="text-xs" data-testid={`brand-kit-tab-${p}`}>
                  <span className="w-2 h-2 rounded-full mr-2" style={{ background: PLATFORM_COLOR[p] }} />
                  {PLATFORM_LABEL[p]}
                </TabsTrigger>
              ))}
            </TabsList>

            {["instagram", "facebook", "tiktok", "linkedin", "pinterest"].map((p) => (
              <TabsContent key={p} value={p}>
                <Card className="border-[var(--studio-border)] bg-[var(--studio-surface)]">
                  <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        {PLATFORM_LABEL[p]}
                        {template.enabled && <Badge className="bg-green-600 text-white">Aktiv</Badge>}
                      </CardTitle>
                      {platformFormat && (
                        <p className="text-xs text-[var(--studio-text-muted)] mt-1">
                          Standard-Format: {platformFormat.sizes[platformFormat.default_format][0]} × {platformFormat.sizes[platformFormat.default_format][1]} px
                          {" · "}Story: {platformFormat.sizes.story[0]} × {platformFormat.sizes.story[1]} px
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Label className="text-sm">Aktiv</Label>
                      <Switch
                        checked={template.enabled}
                        onCheckedChange={toggleEnabled}
                        data-testid={`brand-kit-enable-${p}`}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Logo section */}
                    <SectionCard
                      icon={<ImageIcon className="w-4 h-4" />}
                      title="Logo"
                      hasAsset={!!template.logo?.storage_path}
                      onUpload={(file) => uploadAsset(file, "logo")}
                      onClear={() => updateTemplate("logo", { media_id: null, storage_path: null })}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Position</Label>
                          <Select
                            value={template.logo?.anchor || "bottom-right"}
                            onValueChange={(v) => updateTemplate("logo", { anchor: v })}
                          >
                            <SelectTrigger className="mt-1 bg-[var(--studio-bg)] border-[var(--studio-border)]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(ANCHOR_LABEL).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <NumberField
                          label="Groesse (% der Breite)"
                          value={template.logo?.size_pct ?? 15}
                          min={1} max={50} step={1}
                          onChange={(v) => updateTemplate("logo", { size_pct: v })}
                        />
                        <NumberField
                          label="Opazitaet (0-1)"
                          value={template.logo?.opacity ?? 1}
                          min={0} max={1} step={0.05}
                          onChange={(v) => updateTemplate("logo", { opacity: v })}
                        />
                        <NumberField
                          label="Rand-Abstand (%)"
                          value={template.logo?.padding_pct ?? 4}
                          min={0} max={20} step={0.5}
                          onChange={(v) => updateTemplate("logo", { padding_pct: v })}
                        />
                      </div>
                    </SectionCard>

                    {/* Frame section */}
                    <SectionCard
                      icon={<FrameIcon className="w-4 h-4" />}
                      title="Rahmen (Full-Canvas Overlay)"
                      hasAsset={!!template.frame?.storage_path}
                      onUpload={(file) => uploadAsset(file, "frame")}
                      onClear={() => updateTemplate("frame", { media_id: null, storage_path: null })}
                    >
                      <NumberField
                        label="Opazitaet (0-1)"
                        value={template.frame?.opacity ?? 1}
                        min={0} max={1} step={0.05}
                        onChange={(v) => updateTemplate("frame", { opacity: v })}
                      />
                    </SectionCard>

                    {/* Text section */}
                    <SectionCard
                      icon={<Type className="w-4 h-4" />}
                      title="Textbalken"
                      hasAsset={!!template.text?.content}
                      hideUpload
                      onClear={() => updateTemplate("text", { content: "" })}
                    >
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Text</Label>
                          <Input
                            value={template.text?.content || ""}
                            onChange={(e) => updateTemplate("text", { content: e.target.value })}
                            placeholder="z. B. @strongfit_wetzikon"
                            className="mt-1 bg-[var(--studio-bg)] border-[var(--studio-border)]"
                            data-testid={`brand-kit-text-${p}`}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Position</Label>
                            <Select
                              value={template.text?.anchor || "bottom-left"}
                              onValueChange={(v) => updateTemplate("text", { anchor: v })}
                            >
                              <SelectTrigger className="mt-1 bg-[var(--studio-bg)] border-[var(--studio-border)]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(ANCHOR_LABEL).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <NumberField
                            label="Schriftgroesse (px)"
                            value={template.text?.size_px ?? 48}
                            min={16} max={200} step={2}
                            onChange={(v) => updateTemplate("text", { size_px: v })}
                          />
                          <div>
                            <Label className="text-xs">Textfarbe</Label>
                            <Input
                              type="color"
                              value={template.text?.color || "#FFFFFF"}
                              onChange={(e) => updateTemplate("text", { color: e.target.value })}
                              className="mt-1 h-10 bg-[var(--studio-bg)] border-[var(--studio-border)] p-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Hintergrund (Hex)</Label>
                            <Input
                              value={template.text?.background || "#000000B3"}
                              onChange={(e) => updateTemplate("text", { background: e.target.value })}
                              placeholder="#000000B3"
                              className="mt-1 bg-[var(--studio-bg)] border-[var(--studio-border)]"
                            />
                          </div>
                        </div>
                      </div>
                    </SectionCard>

                    <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[var(--studio-border)]">
                      <Button onClick={saveTemplate} disabled={saving} data-testid="brand-kit-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Speichern
                      </Button>
                      <Button variant="outline" onClick={clearTemplate}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Zuruecksetzen
                      </Button>
                      <Button variant="secondary" onClick={runAiGenerate} disabled={generating} data-testid="brand-kit-ai-generate">
                        {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Auf alle 4 Plattformen uebernehmen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {/* Live preview column */}
        <div>
          <Card className="border-[var(--studio-border)] bg-[var(--studio-surface)] sticky top-4">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                Live-Vorschau
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => refreshPreview()}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Brand Kit Vorschau"
                  className="w-full rounded-lg border border-[var(--studio-border)]"
                  data-testid="brand-kit-preview"
                />
              ) : (
                <div className="aspect-square rounded-lg bg-[var(--studio-bg)] flex items-center justify-center text-[var(--studio-text-muted)]">
                  Vorschau wird geladen ...
                </div>
              )}
              <p className="text-xs text-[var(--studio-text-muted)] mt-3">
                Auf einer leeren grauen Canvas-Vorlage in der echten Pixelgroesse der gewaehlten Plattform.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, min, max, step, onChange }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        value={value}
        min={min} max={max} step={step}
        onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
        className="mt-1 bg-[var(--studio-bg)] border-[var(--studio-border)]"
      />
    </div>
  );
}

function SectionCard({ icon, title, hasAsset, onUpload, onClear, hideUpload, children }) {
  const inputRef = useRef(null);
  return (
    <div className="rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg)] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          {icon}
          <span>{title}</span>
          {hasAsset && <Badge variant="outline" className="ml-2 text-xs">gesetzt</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {!hideUpload && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => onUpload && onUpload(e.target.files?.[0])}
              />
              <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1" />
                {hasAsset ? "Ersetzen" : "Hochladen"}
              </Button>
            </>
          )}
          {hasAsset && onClear && (
            <Button variant="ghost" size="sm" onClick={onClear}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
